'use client';

import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useState } from 'react';

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
};

type ToolbarButton = {
  label: string;
  title: string;
  action: () => void;
  active: boolean;
};

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  label,
  disabled,
}: Props) {
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {},
        orderedList: {},
        listItem: {},
        underline: {},
        link: {
          openOnClick: false,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: value ?? '',
    editable: !disabled,
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value ?? '', { emitUpdate: false });
    }
  }, [value, editor]);

  const applyLink = () => {
    if (!editor) return;
    if (linkUrl) {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const handleLinkButton = () => {
    if (!editor) return;
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const existing = editor.getAttributes('link').href ?? '';
    setLinkUrl(existing);
    setShowLinkInput((v) => !v);
  };

  if (!editor) return null;

  const toolbarButtons: ToolbarButton[] = [
    {
      label: 'B',
      title: 'Fett',
      action: () => editor.chain().focus().toggleBold().run(),
      active: editor.isActive('bold'),
    },
    {
      label: 'I',
      title: 'Kursiv',
      action: () => editor.chain().focus().toggleItalic().run(),
      active: editor.isActive('italic'),
    },
    {
      label: 'U',
      title: 'Unterstrichen',
      action: () => editor.chain().focus().toggleUnderline().run(),
      active: editor.isActive('underline'),
    },
    {
      label: '•',
      title: 'Aufzählung',
      action: () => editor.chain().focus().toggleBulletList().run(),
      active: editor.isActive('bulletList'),
    },
    {
      label: '1.',
      title: 'Nummerierung',
      action: () => editor.chain().focus().toggleOrderedList().run(),
      active: editor.isActive('orderedList'),
    },
    {
      label: '🔗',
      title: 'Link',
      action: handleLinkButton,
      active: editor.isActive('link'),
    },
  ];

  return (
    <div className="flex flex-col gap-1 w-full">
      {label && <label>{label}</label>}

      <div
        className={`rounded-1_5 border border-gray-tint-5 bg-white ${isFocused ? 'border-int-blue' : ''}`}
        onFocusCapture={() => setIsFocused(true)}
        onBlurCapture={() => setIsFocused(false)}
      >
        <div className="flex flex-wrap items-center gap-0_5 padding-h-0_5 padding-v-0_5 border-b border-gray-tint-5">
          {toolbarButtons.map(({ label, title, action, active }) => (
            <button
              key={title}
              type="button"
              title={title}
              disabled={disabled}
              onClick={action}
              className={`rounded border-none cursor-pointer padding-h-1_5 padding-v-0_5 ${
                active ? 'bg-int-blue text-white' : 'text-gray'
              } ${label === 'B' ? 'font-bold' : ''} ${label === 'I' ? 'italic' : ''} ${label === 'U' ? 'underline' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        {showLinkInput && (
          <div className="flex items-center gap-1 padding-h-0_5 padding-v-0_5 rounded-1_5 border-gray-tint-5">
            <input
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyLink()}
              placeholder="https://example.com"
              autoFocus
              className="grow-1 rounded-1 border border-gray-tint-3 padding-h-1_5 padding-v-0_5 rounded-1_5"
            />
            <button
              type="button"
              onClick={applyLink}
              className="rounded border-none cursor-pointer padding-h-1_5 padding-v-0_5 bg-int-blue text-white"
            >
              OK
            </button>
            <button
              type="button"
              onClick={() => setShowLinkInput(false)}
              className="rounded border border-gray-tint-3 cursor-pointer padding-h-1_5 padding-v-0_5"
            >
              ✕
            </button>
          </div>
        )}

        <EditorContent editor={editor} className="padding-h-2 padding-v-1_5" />
      </div>
    </div>
  );
}
